<template>
    <div
        ref="el"
        class="structure-block"
        :class="blockClasses"
        :style="backgroundStyles"
    >
        <div :style="`padding: ${padding}`" :class="innerClasses">
            <component :is="component" :content="content.slots.length === 1 ? content.slots[0] : content.slots" :count="count" />
        </div>
    </div>
</template>

<script setup lang="ts">
import {computed, ref, shallowRef, onMounted} from 'vue'
import type {HblBlock} from '@/utils/types'
import {
    hblToUpperCamelCase,
    hblRegisterIntersectionObserver,
    hblGetStructureBackgroundStyles
} from '@/utils/helper'

const props = defineProps<{
    count?: number,
    content: HblBlock,
    ssr?: boolean,
    sectionSizingMode?: string
}>()

const {backgroundStyles} = hblGetStructureBackgroundStyles(props.content)

const blockClasses = computed(() => {
    const height = typeof component.value === 'undefined' ? 'min-h-[144px]' : ''
    const classes = props?.content?.cssClass != null ? props.content.cssClass : ''

    // Visibility classes for mobile/tablet/desktop
    const visibilityClasses = []
    if (props.content.visibility) {
        // Mobile visibility
        if (props.content.visibility.mobile) {
            visibilityClasses.push('block')
        } else {
            visibilityClasses.push('hidden')
        }

        // Tablet visibility
        if (props.content.visibility.tablet) {
            visibilityClasses.push('md:block')
        } else {
            visibilityClasses.push('md:hidden')
        }

        // Desktop visibility
        if (props.content.visibility.desktop) {
            visibilityClasses.push('xl:block')
        } else {
            visibilityClasses.push('xl:hidden')
        }
    }

    return [height, classes, ...visibilityClasses].filter(Boolean).join(' ')
})

const innerClasses = computed(() => {
    // Add negative margins for hero-banner block in fullwidth sections
    if (props.content.type === 'hero-banner' && props.sectionSizingMode === 'full_width') {
        return '-mx-4'
    }
    return ''
})

// If block contains exactly one slot, directly load SLOT. If more than one slots then load BLOCK
const compName = computed(() => {
    let name = `StructureBlock${hblToUpperCamelCase(props.content.type)}`

    if (props.content.slots.length === 1) {
        name = `StructureSlot${hblToUpperCamelCase(props.content.slots[0].type)}`
    }

    return name
})

const padding = computed(() => {
    // @ts-ignore
    const top = props.content?.marginTop ? props.content?.marginTop : '0px'
    // @ts-ignore
    const bottom = props.content?.marginBottom ? props.content?.marginBottom : '0px'
    // @ts-ignore
    const right = props.content?.marginRight ? props.content?.marginRight : '0px'
    // @ts-ignore
    const left = props.content?.marginLeft ? props.content?.marginLeft : '0px'

    return top + ' ' + right + ' ' + bottom + ' ' + left
})

// Component to render - use string name for globally registered components
const component = shallowRef<string>()

// Render first two section server side (SEO relevant hero elements) or with ssr flag
const componentSSR = props.count <= 1 || props.count == null || props.ssr;
if (componentSSR) {
    component.value = compName.value
}

// Lazy load other sections via intersection observer
const el = ref()
onMounted(() => {
    if (!componentSSR) {
        setTimeout(() => {
            hblRegisterIntersectionObserver(el.value, () => {
                component.value = compName.value
            })
        }, 300)
    }
})
</script>
